import { $window } from '@umu/umu-dom';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { AnnotationsLayer } from '../services/annotationsLayer';
import { renderWithCJKFix } from '../services/iosCjkRender';
import { RENDER_TYPE } from '../types';
import { getPixelRatio, isIOSSafari } from '../utils/common';
import { getViewport } from '../utils/pdf';

// ===== 分块渲染（Tile Rendering）常量 =====

/** 单块 tile 的 CSS 边长 */
const TILE_SIZE_CSS_IOS = 1024;
const TILE_SIZE_CSS_STD = 2048;
/** 同时渲染的最大 tile 数 */
const TILE_PARALLEL_IOS = 1;
const TILE_PARALLEL_STD = 2;
/** iOS 触发分块的物理像素阈值 */
const TILE_THRESHOLD_IOS = 16_000_000;
/** 非 iOS 触发分块的单边物理像素阈值 */
const TILE_THRESHOLD_STD_DIM = 7680;
/** 非 iOS 触发分块的总物理像素阈值 */
const TILE_THRESHOLD_STD_PIXELS = 50_000_000;
/** 分块模式滞后系数 */
const TILE_HYSTERESIS = 0.85;
/** 大分辨率文档放大时跳过重渲染的 scale 比值上限 */
const LARGE_DOC_SKIP_RENDER_RATIO = 1.5;
/**
 * iOS 每页分块渲染的总像素预算（字节）。
 *
 * 分块渲染的总内存 = cssW × cssH × 4（所有分块面积之和等于整页面积），
 * 与分块大小无关。iOS WebKit IOSurface 总预算约 240 MB，全屏垂直模式
 * 同时可见 2~3 页，每页上限取 80 MB 可保证总量在安全范围内。
 *
 * 超出预算时跳过重渲染（不更新 tileLayout），usePdfPageScale 持续以
 * CSS transform 将旧内容拉伸到新视觉尺寸——模糊但可见，不会白屏。
 */
const IOS_TILE_MEMORY_BUDGET = 80_000_000;

/**
 * 检查当前缩放尺寸是否超出 iOS 分块渲染内存预算。
 * 超出时调用方应跳过本次渲染，由 usePdfPageScale 的 CSS transform 补偿视觉尺寸。
 */
function exceedsIosTileMemoryBudget(effPhysW: number, effPhysH: number, effectivePixelRatio: number): boolean {
    const cssW = Math.floor(effPhysW / effectivePixelRatio);
    const cssH = Math.floor(effPhysH / effectivePixelRatio);
    return cssW * cssH * 4 > IOS_TILE_MEMORY_BUDGET;
}

/** 根据物理像素尺寸判断是否需要进入分块渲染模式 */
function computeNeedsTile(
    effPhysW: number,
    effPhysH: number,
    effPhysPixels: number,
    exitFactor: number,
    isIOS: boolean,
): boolean {
    if (isIOS) {
        return effPhysPixels > TILE_THRESHOLD_IOS * exitFactor;
    }
    return (
        effPhysW > TILE_THRESHOLD_STD_DIM * exitFactor ||
        effPhysH > TILE_THRESHOLD_STD_DIM * exitFactor ||
        effPhysPixels > TILE_THRESHOLD_STD_PIXELS * exitFactor
    );
}

export interface TileInfo {
    col: number;
    row: number;
    cssX: number;
    cssY: number;
    cssW: number;
    cssH: number;
}

export interface TileLayout {
    tiles: TileInfo[];
    pageCssW: number;
    pageCssH: number;
    pixelRatio: number;
    /** 内存超限时降低倍率渲染的实际 scale（< 视觉 scale），由 CSS transform 补偿放大 */
    renderScale?: number;
}

/**
 * 校验 Canvas 页面是否渲染成功（三级校验）。
 * 1. 尺寸校验
 * 2. alpha > 0 像素内容校验（白纸 PDF 背景也是 alpha=255）
 * 3. 跨域 SecurityError 降级为"已渲染"
 */
function validatePageRender(canvas: HTMLCanvasElement): boolean {
    if (canvas.width <= 0 || canvas.height <= 0) return false;
    try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        const { width, height } = canvas;
        const samplePoints: [number, number][] = [
            [Math.floor(width * 0.1), Math.floor(height * 0.1)],
            [Math.floor(width * 0.9), Math.floor(height * 0.1)],
            [Math.floor(width * 0.5), Math.floor(height * 0.5)],
            [Math.floor(width * 0.1), Math.floor(height * 0.9)],
            [Math.floor(width * 0.9), Math.floor(height * 0.9)],
        ];
        return samplePoints.some(([x, y]) => ctx.getImageData(x, y, 1, 1).data[3] > 0);
    } catch (e) {
        if (e instanceof DOMException && e.name === 'SecurityError') return true;
        return false;
    }
}

function cancelPendingRenderFn(
    renderTaskRef: { current: { cancel?: () => void } | null },
    isCanvasRendering: { current: boolean }
): void {
    if (isCanvasRendering.current && renderTaskRef.current) {
        try {
            renderTaskRef.current.cancel?.();
        } catch (_e) {
            // ignore
        }
    }
    isCanvasRendering.current = false;
}

function computeTileLayout(cssW: number, cssH: number, pixelRatio: number, tileSizeCss: number, renderScale?: number): TileLayout {
    const numCols = Math.ceil(cssW / tileSizeCss);
    const numRows = Math.ceil(cssH / tileSizeCss);
    const tiles: TileInfo[] = [];
    for (let row = 0; row < numRows; row++) {
        for (let col = 0; col < numCols; col++) {
            const cssX = col * tileSizeCss;
            const cssY = row * tileSizeCss;
            tiles.push({
                col,
                row,
                cssX,
                cssY,
                cssW: Math.min(tileSizeCss, cssW - cssX),
                cssH: Math.min(tileSizeCss, cssH - cssY),
            });
        }
    }
    return { tiles, pageCssW: cssW, pageCssH: cssH, pixelRatio, renderScale };
}

/** 将离屏 canvas 内容提交到 tile canvas 上（从 renderOneTile 提取以降低认知复杂度） */
function commitTileToCanvas(
    canvas: HTMLCanvasElement,
    offscreen: HTMLCanvasElement,
    physW: number,
    physH: number,
    tile: TileInfo,
    renderedTiles: Set<string>
): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = physW;
    canvas.height = physH;
    canvas.style.width = `${tile.cssW}px`;
    canvas.style.height = `${tile.cssH}px`;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(offscreen, 0, 0);
    canvas.style.visibility = '';
    renderedTiles.add(`${tile.col}-${tile.row}`);
}

export interface UseCanvasRendererProps {
    scale: number;
    pageNum: number;
    screenState?: string;
    renderPixelRadio?: number;
    renderType: RENDER_TYPE;
    canvasRef: RefObject<HTMLCanvasElement>;
    annotationLayerRef?: RefObject<HTMLDivElement>;
    visible: boolean;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
    pdfDoc: any;
    onRenderSuccess?: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: error type
    onRenderError?: (error: any) => void;
    onInternalLinkClick?: (pageNum: number) => void;
    enableCjkFix?: boolean;
}

/**
 * Canvas 渲染 Hook（含 Tile 分块逻辑）
 *
 * 从 usePdfRenderer 拆分：封装所有 Canvas 路径，包括 iOS DPR 降级、Tile 阈值决策、
 * 离屏 canvas 渲染与原子替换。
 */
export function useCanvasRenderer(props: UseCanvasRendererProps) {
    const {
        scale,
        pageNum,
        renderPixelRadio,
        renderType,
        canvasRef,
        annotationLayerRef,
        visible,
        pdfDoc,
        onRenderSuccess,
        onRenderError,
        onInternalLinkClick,
        enableCjkFix,
    } = props;

    // 用 ref 持有最新值，避免 renderByCanvas / renderOneTile useCallback 闭包捕获旧值
    const enableCjkFixRef = useRef(enableCjkFix ?? false);
    enableCjkFixRef.current = enableCjkFix ?? false;

    // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
    const renderTaskRef = useRef<any>(null);
    const isCanvasRendering = useRef(false);

    const [tileLayout, setTileLayout] = useState<TileLayout | null>(null);
    const isTileModeRef = useRef(false);
    const prevPageNumRef = useRef(0);
    const renderedTilesRef = useRef<Set<string>>(new Set());
    const tileCanvasMapRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
    const lastDpr1RenderedScaleRef = useRef<number | null>(null);

    const registerTileCanvas = useCallback((col: number, row: number, el: HTMLCanvasElement | null) => {
        const key = `${col}-${row}`;
        if (el) {
            tileCanvasMapRef.current.set(key, el);
        } else {
            tileCanvasMapRef.current.delete(key);
        }
    }, []);

    /**
     * 低级 Canvas 渲染：渲染到离屏 canvas 后原子替换主 canvas，避免空白闪烁。
     * 供 renderCanvas 和 SVG 回退路径共用。
     */
    const renderByCanvas = useCallback(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        async (page: any, overridePixelRatio?: number) => {
            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            cancelPendingRenderFn(renderTaskRef, isCanvasRendering);
            isCanvasRendering.current = true;

            // 每次新建离屏 canvas，避免复用同一个 context 对象触发 pdfjs 的
            // "Cannot use the same canvas during multiple render() operations" 错误。
            // cancel() 是异步完成的（pdfjs 在下一个 checkpoint 才真正停止），
            // 若复用旧 canvas，新的 page.render() 会与尚未释放的上一次渲染争用同一 context。
            const offscreen = document.createElement('canvas');
            try {
                const pixelRatio = overridePixelRatio ?? (renderPixelRadio || getPixelRatio());
                const viewport = getViewport(page, scale * pixelRatio);
                const physW = Math.floor(viewport.width);
                const physH = Math.floor(viewport.height);
                const cssW = Math.floor(viewport.width / pixelRatio);
                const cssH = Math.floor(viewport.height / pixelRatio);

                offscreen.width = physW;
                offscreen.height = physH;
                const offCtx = offscreen.getContext('2d');
                if (!offCtx) {
                    throw new Error(
                        '[canvasRenderer] offscreen canvas 2D context unavailable (Safari GPU memory limit?)'
                    );
                }

                let finalOffscreen = offscreen;
                if (enableCjkFixRef.current) {
                    finalOffscreen = await renderWithCJKFix(page, offscreen, offCtx, viewport);
                } else {
                    const renderTask = page.render({ canvasContext: offCtx, viewport });
                    renderTaskRef.current = renderTask;
                    await renderTask.promise;
                }

                const context = canvas.getContext('2d');
                if (!context) {
                    throw new Error('[canvasRenderer] main canvas 2D context unavailable');
                }
                canvas.width = physW;
                canvas.height = physH;
                canvas.style.width = `${cssW}px`;
                canvas.style.height = `${cssH}px`;
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = 'high';
                context.drawImage(finalOffscreen, 0, 0);
                // 提交后立即释放离屏 canvas 的 GPU（IOSurface）内存。
                if (finalOffscreen !== offscreen) finalOffscreen.width = 0;
                offscreen.width = 0;

                if (!validatePageRender(canvas)) {
                    // 渲染"成功"但 canvas 仍为空白，说明 IOSurface 上下文已被 WebKit 回收。
                    // 抛出异常以触发降级链（Canvas → SVG → Image），而非静默留白。
                    throw new Error('[canvasRenderer] blank canvas after render: IOSurface context may have been lost');
                }

                if (annotationLayerRef?.current) {
                    new AnnotationsLayer(
                        $window.pdfjsLib,
                        page,
                        viewport,
                        canvas,
                        annotationLayerRef.current,
                        scale,
                        pdfDoc,
                        onInternalLinkClick
                    );
                }
                onRenderSuccess?.();
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e?.name !== 'RenderingCancelledException') {
                    // Re-throw so callers can implement fallback chains.
                    // Callers (useSvgRenderer, usePdfRenderer) handle onRenderError themselves.
                    throw err;
                }
            } finally {
                // 取消或报错路径同样释放 GPU 内存（已在 drawImage 后释放时为 no-op）。
                offscreen.width = 0;
                isCanvasRendering.current = false;
                renderTaskRef.current = null;
            }
        },
        [scale, canvasRef, annotationLayerRef, onRenderSuccess, renderPixelRadio, pageNum]
    );

    /** 取消正在进行的 canvas 渲染任务 */
    const cancelPending = useCallback(() => {
        cancelPendingRenderFn(renderTaskRef, isCanvasRendering);
    }, []);

    /**
     * iOS DPR 降级路径：当物理像素超阈值时，尝试以 DPR=1 渲染或跳过重渲染。
     * 返回 'done'：已处理，renderCanvas 应直接 return；
     * 返回 1：DPR=1 仍超阈值，effectivePixelRatio 应设为 1 继续走 Tile 路径；
     * 返回 false：不适用 iOS DPR 降级。
     */
    const tryIosDprFallback = useCallback(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        async (page: any, pixelRatio: number, physPixels: number): Promise<'done' | 1 | false> => {
            if (!isIOSSafari || pixelRatio <= 1 || physPixels <= TILE_THRESHOLD_IOS) return false;
            const dpr1Viewport = getViewport(page, scale);
            const dpr1Pixels = dpr1Viewport.width * dpr1Viewport.height;
            if (dpr1Pixels > TILE_THRESHOLD_IOS) {
                return 1; // DPR=1 仍超阈值，走 tile
            }

            // 当前处于分块模式时主 canvas 不在 DOM（PdfPage 仅在 !tileLayout 时渲染主 canvas），
            // DPR=1 canvas 渲染无法执行。跳过本路径，由 tryApplyTileMode 按分块逻辑处理。
            if (!canvasRef.current) {
                return false;
            }

            const lastRenderedScale = lastDpr1RenderedScaleRef.current;
            const canSkip =
                lastRenderedScale !== null &&
                scale > lastRenderedScale &&
                scale / lastRenderedScale <= LARGE_DOC_SKIP_RENDER_RATIO &&
                canvasRef.current !== null &&
                canvasRef.current.width > 0;
            if (canSkip && canvasRef.current) {
                canvasRef.current.style.width = `${Math.floor(dpr1Viewport.width)}px`;
                canvasRef.current.style.height = `${Math.floor(dpr1Viewport.height)}px`;
                return 'done';
            }

            isTileModeRef.current = false;
            renderedTilesRef.current.clear();
            setTileLayout(null);
            if (canvasRef.current) canvasRef.current.style.display = 'block';
            await renderByCanvas(page, 1);
            lastDpr1RenderedScaleRef.current = scale;
            return 'done';
        },
        [scale, pageNum, canvasRef, renderByCanvas]
    );

    /**
     * iOS 内存超限且无既存 tile 时，按预算降低倍率计算分块布局。
     * reducedScale = scale × sqrt(budget / currentMemory)，使总内存恰好等于预算。
     * 渲染完成后由 usePdfPageScale 的 CSS transform（scale / reducedScale）补偿视觉尺寸。
     */
    const applyReducedScaleTileLayout = useCallback(
        (effPhysW: number, effPhysH: number, effectivePixelRatio: number): void => {
            const cssW0 = Math.floor(effPhysW / effectivePixelRatio);
            const cssH0 = Math.floor(effPhysH / effectivePixelRatio);
            const scaleFactor = Math.sqrt(IOS_TILE_MEMORY_BUDGET / (cssW0 * cssH0 * 4));
            const reducedScale = scale * scaleFactor;
            const reducedCssW = Math.floor(cssW0 * scaleFactor);
            const reducedCssH = Math.floor(cssH0 * scaleFactor);
            isTileModeRef.current = true;
            setTileLayout((prev) =>
                prev?.pageCssW === reducedCssW &&
                    prev?.pageCssH === reducedCssH &&
                    prev?.pixelRatio === effectivePixelRatio &&
                    prev?.renderScale === reducedScale
                    ? prev
                    : computeTileLayout(reducedCssW, reducedCssH, effectivePixelRatio, TILE_SIZE_CSS_IOS, reducedScale)
            );
            if (canvasRef.current) canvasRef.current.style.display = 'none';
        },
        [scale, canvasRef]
    );

    /**
     * 分块模式决策：接收有效物理尺寸，若需分块则设置 tileLayout 并返回 true。
     * 返回 true 时调用方（renderCanvas）应立即 return。
     *
     * - 通常超阈值：按当前倍率计算分块布局
     * - iOS 内存超限 + 已有 tile：跳过重渲染，由 CSS transform 拉伸旧内容
     * - iOS 内存超限 + 无既存 tile：按预算降低倍率分块（reduced-scale）
     */
    const tryApplyTileMode = useCallback(
        (effPhysW: number, effPhysH: number, effPhysPixels: number, effectivePixelRatio: number): boolean => {
            const exitFactor = isTileModeRef.current ? TILE_HYSTERESIS : 1;
            const needsTile = computeNeedsTile(effPhysW, effPhysH, effPhysPixels, exitFactor, isIOSSafari);
            if (!needsTile) return false;

            if (isIOSSafari && exceedsIosTileMemoryBudget(effPhysW, effPhysH, effectivePixelRatio)) {
                if (renderedTilesRef.current.size > 0) {
                    // 非 tile→tile 转换（如全屏退出）后 isTileModeRef 可能被清为 false，需还原，
                    // 确保后续 exitFactor 使用 TILE_HYSTERESIS 而非 1。
                    isTileModeRef.current = true;
                    return true;
                }
                applyReducedScaleTileLayout(effPhysW, effPhysH, effectivePixelRatio);
                return true;
            }

            isTileModeRef.current = true;
            const cssW = Math.floor(effPhysW / effectivePixelRatio);
            const cssH = Math.floor(effPhysH / effectivePixelRatio);
            const tileSizeCss = isIOSSafari ? TILE_SIZE_CSS_IOS : TILE_SIZE_CSS_STD;
            setTileLayout((prev) =>
                prev?.pageCssW === cssW && prev?.pageCssH === cssH
                    && prev?.pixelRatio === effectivePixelRatio && prev?.renderScale === scale
                    ? prev
                    : computeTileLayout(cssW, cssH, effectivePixelRatio, tileSizeCss, scale)
            );
            if (canvasRef.current) canvasRef.current.style.display = 'none';
            return true;
        },
        [scale, canvasRef, applyReducedScaleTileLayout]
    );

    /**
     * 高级 Canvas 渲染入口：处理 iOS DPR 降级 + Tile 阈值决策 + 大分辨率跳过逻辑。
     * 从主 effect 调用；resetForNewPage 应在调用前执行。
     */
    const renderCanvas = useCallback(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        async (page: any) => {
            const pixelRatio = renderPixelRadio || getPixelRatio();
            const testViewport = getViewport(page, scale * pixelRatio);
            const physW = testViewport.width;
            const physH = testViewport.height;
            const physPixels = physW * physH;

            const dprResult = await tryIosDprFallback(page, pixelRatio, physPixels);
            if (dprResult === 'done') {
                return;
            }
            const effectivePixelRatio = dprResult === 1 ? 1 : pixelRatio;

            const effPhysW =
                effectivePixelRatio === pixelRatio
                    ? physW
                    : Math.floor(getViewport(page, scale * effectivePixelRatio).width);
            const effPhysH =
                effectivePixelRatio === pixelRatio
                    ? physH
                    : Math.floor(getViewport(page, scale * effectivePixelRatio).height);
            const effPhysPixels = effPhysW * effPhysH;

            if (tryApplyTileMode(effPhysW, effPhysH, effPhysPixels, effectivePixelRatio)) {
                return;
            }

            isTileModeRef.current = false;
            if (!canvasRef.current) {
                // canvasRef.current 为 null 说明 tileLayout 非空——PdfPage 仅在 !tileLayout 时渲染主 canvas。
                // 此时无法切到 non-tile 模式（主 canvas 不在 DOM，renderByCanvas 会空跑），
                // 也不能单纯保留旧 tileLayout（缩放后旧尺寸会导致容器过宽/过窄）。
                // 解决方案：留在 tile 模式，但用当前 scale 的实际尺寸更新 tileLayout，
                // 保证 .pdf-page-container 宽度 = 当前 scale 对应的页面 CSS 宽度。
                // 尺寸较小时只产生 1 块 tile，效果等同于单 canvas。
                const cssW = Math.floor(effPhysW / effectivePixelRatio);
                const cssH = Math.floor(effPhysH / effectivePixelRatio);
                const tileSizeCss = isIOSSafari ? TILE_SIZE_CSS_IOS : TILE_SIZE_CSS_STD;
                isTileModeRef.current = true;
                setTileLayout((prev) =>
                    prev && prev.pageCssW === cssW && prev.pageCssH === cssH
                        && prev.pixelRatio === effectivePixelRatio && prev.renderScale === scale
                        ? prev
                        : computeTileLayout(cssW, cssH, effectivePixelRatio, tileSizeCss, scale)
                );
                lastDpr1RenderedScaleRef.current = null;
                return;
            }
            renderedTilesRef.current.clear();
            setTileLayout(null);
            if (canvasRef.current) canvasRef.current.style.display = 'block';
            await renderByCanvas(page);
            lastDpr1RenderedScaleRef.current = null;
        },
        [scale, pageNum, renderPixelRadio, canvasRef, renderByCanvas, tryIosDprFallback, tryApplyTileMode]
    );

    /**
     * 页码变化时重置分块状态（由主 effect 在 getPage 前调用）
     */
    const resetForNewPage = useCallback((newPageNum: number) => {
        if (prevPageNumRef.current !== newPageNum) {
            prevPageNumRef.current = newPageNum;
            isTileModeRef.current = false;
            renderedTilesRef.current.clear();
            lastDpr1RenderedScaleRef.current = null;
        }
    }, []);

    /** 渲染单块 Tile（从 useEffect 内提取以降低认知复杂度） */
    const renderOneTile = useCallback(
        async (
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
            page: any,
            tile: TileInfo,
            pixelRatio: number,
            tileRenderScale: number,
            signal: AbortSignal,
            activeTasks: Array<{ cancel?: () => void }>
        ): Promise<void> => {
            if (signal.aborted) return;
            const canvas = tileCanvasMapRef.current.get(`${tile.col}-${tile.row}`);
            if (!canvas) return;

            const physW = Math.round(tile.cssW * pixelRatio);
            const physH = Math.round(tile.cssH * pixelRatio);

            const offscreen = document.createElement('canvas');
            offscreen.width = physW;
            offscreen.height = physH;
            const offCtx = offscreen.getContext('2d');
            if (!offCtx) {
                throw new Error('[canvasRenderer] tile offscreen canvas 2D context unavailable');
            }

            const tileViewport = page.getViewport({
                scale: tileRenderScale * pixelRatio,
                offsetX: -(tile.cssX * pixelRatio),
                offsetY: -(tile.cssY * pixelRatio),
            });

            let renderSource = offscreen;
            try {
                if (enableCjkFixRef.current) {
                    renderSource = await renderWithCJKFix(page, offscreen, offCtx, tileViewport);
                } else {
                    const task = page.render({ canvasContext: offCtx, viewport: tileViewport });
                    activeTasks.push(task);
                    await task.promise;
                }
                if (!signal.aborted) {
                    commitTileToCanvas(canvas, renderSource, physW, physH, tile, renderedTilesRef.current);
                }
            } catch (err: unknown) {
                if (!signal.aborted && (err as { name?: string })?.name !== 'RenderingCancelledException') {
                    throw err;
                }
            } finally {
                if (renderSource !== offscreen) renderSource.width = 0;
                offscreen.width = 0;
                offscreen.height = 0;
            }
        },
        []
    );

    // 实际 tile 渲染 scale：reduced-scale 模式取 tileLayout.renderScale（固定值），
    // 普通 tile 模式取 scale。作为 effect dep 替代 scale，避免 reduced-scale 模式下
    // scale 变化触发无意义的 abort→restart 循环（渲染内容相同，却中断正在进行的 tile 渲染）。
    const actualTileRenderScale = tileLayout?.renderScale ?? scale;

    // ===== 分块渲染 Effect =====
    useEffect(() => {
        if (!(tileLayout && visible && pdfDoc && pageNum && actualTileRenderScale) || actualTileRenderScale <= 0 || Number.isNaN(actualTileRenderScale)) {
            return;
        }
        if (renderType !== RENDER_TYPE.CANVAS) {
            return;
        }

        const ac = new AbortController();
        const { signal } = ac;
        const activeTasks: Array<{ cancel?: () => void }> = [];

        const renderTiles = async () => {
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
            const page: any = await pdfDoc.getPage(pageNum);
            if (signal.aborted) return;

            const { tiles, pixelRatio } = tileLayout;

            for (const tile of tiles) {
                const key = `${tile.col}-${tile.row}`;
                const canvas = tileCanvasMapRef.current.get(key);
                if (canvas && !renderedTilesRef.current.has(key)) {
                    canvas.style.visibility = 'hidden';
                }
            }

            const parallel = isIOSSafari ? TILE_PARALLEL_IOS : TILE_PARALLEL_STD;
            for (let i = 0; i < tiles.length; i += parallel) {
                if (signal.aborted) break;
                const batch = tiles.slice(i, i + parallel);
                await Promise.all(batch.map((tile) => renderOneTile(page, tile, pixelRatio, actualTileRenderScale, signal, activeTasks)));
            }

            if (!signal.aborted) {
                onRenderSuccess?.();
            }
        };

        renderTiles().catch((err: unknown) => {
            // RenderingCancelledException 是正常取消（effect 清理触发），不计入错误
            if (!signal.aborted && (err as { name?: string })?.name !== 'RenderingCancelledException') {
                // 分块渲染失败时重置 tile 状态，使降级链（SVG / Image）能在非分块模式下正常渲染。
                // 若保留 tileLayout，降级后的 SVG/Image 内容仍会套用分块专属的 CSS transform，
                // 导致 .pdf-page-container 宽度错误，内容不可见。
                isTileModeRef.current = false;
                renderedTilesRef.current.clear();
                setTileLayout(null);
                if (canvasRef.current) canvasRef.current.style.display = 'block';
                onRenderError?.(err);
            }
        });

        return () => {
            ac.abort();
            for (const task of activeTasks) {
                try {
                    task.cancel?.();
                } catch (_e) {
                    // ignore
                }
            }
            // 不在此处清空 tile canvas 尺寸：
            // 若重新渲染失败（如全屏切换时放大倍率超限），保留旧内容可避免白屏，
            // GPU 内存释放由"页面不可见"effect 和组件卸载 effect 负责。
        };
    }, [
        tileLayout,
        visible,
        pdfDoc,
        pageNum,
        actualTileRenderScale,
        // 用 actualTileRenderScale 替代 scale：
        // reduced-scale 模式下 actualTileRenderScale = tileLayout.renderScale（固定），
        // scale 变化不会触发 effect 重跑，避免无意义的 abort→restart 导致 iOS 上 tile 渲染中断（抖动+右侧丢失）。
        // 普通 tile 模式下 actualTileRenderScale = scale，行为与之前一致。
        //
        // screenState 不在此处列出：effect 内部不使用 screenState，
        // 不应因全屏切换而重新触发分块渲染（会在已有 tiles 时中断并重渲染，加剧 iOS 内存压力）。
        // 全屏切换引发的 scale/tileLayout 变化已由对应 dep 覆盖。
        renderType,
        onRenderSuccess,
        onRenderError,
        renderOneTile,
    ]);

    // ===== 单 canvas 卸载时释放 IOSurface =====
    useEffect(() => {
        return () => {
            if (canvasRef.current) {
                canvasRef.current.width = 0;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ===== 页面不可见时释放 tile canvas GPU 内存 =====
    useEffect(() => {
        if (!visible && tileLayout) {
            for (const tile of tileLayout.tiles) {
                const canvas = tileCanvasMapRef.current.get(`${tile.col}-${tile.row}`);
                if (canvas) {
                    canvas.width = 0;
                    canvas.height = 0;
                }
            }
        }
    }, [visible, tileLayout]);

    return { renderByCanvas, renderCanvas, tileLayout, registerTileCanvas, cancelPending, resetForNewPage };
}
